import { CompanySettings } from "app/containers/AdminConsole/types";
import {
  UserAbsence,
  UserAbsences,
  UserVisibilitySetting,
  UserVisibilitySettings,
} from "./types";
import { UserInfo } from "../Global/types";

export const getUserVisibilityBannerText = ({
  companySettings,
  userVisibilitySettings,
  isAdmin,
  isCurrentUser,
  usersName,
  userVisibilityKey,
  reportOrGuide = "report",
}: {
  companySettings: CompanySettings | null;
  userVisibilitySettings: UserVisibilitySettings | null;
  isAdmin: boolean;
  isCurrentUser: boolean;
  usersName: string;
  userVisibilityKey: keyof UserVisibilitySettings;
  reportOrGuide?: "report" | "guide";
}) => {
  const { allowVisibilityPermissionOverride } = companySettings || {};
  const userVisibilitySetting = userVisibilitySettings?.[userVisibilityKey];

  const visibilitySet = getUserVisibilitySetText({
    userVisibilitySetting,
    hideSelf: userVisibilityKey !== "Workplace Insights",
    selfName: usersName,
    isCurrentUser,
  });

  if (isAdmin) {
    return (
      <p>
        This {reportOrGuide} is visible to <b>{visibilitySet}</b>.{" "}
        {isCurrentUser && allowVisibilityPermissionOverride
          ? "You can change these visibility settings."
          : "As an admin, you can configure organization-wide visibility defaults."}
      </p>
    );
  }

  if (isCurrentUser) {
    return (
      <p>
        This {reportOrGuide} is visible to <b>{visibilitySet}</b>.{" "}
        {allowVisibilityPermissionOverride
          ? "You can change these visibility settings."
          : "Only admins can configure organization-wide visibility defaults."}
      </p>
    );
  }

  return (
    <p>
      This {reportOrGuide} is visible to <b>{visibilitySet}</b>.{" "}
      {allowVisibilityPermissionOverride
        ? `${usersName} can change these visibility settings. Additionally admins can configure organization-wide visibility defaults.`
        : "Only admins can configure organization-wide visibility defaults."}
    </p>
  );
};

export const getUserVisibilitySetText = ({
  userVisibilitySetting,
  hideSelf = false,
  selfName = "you",
  hideManager = false,
  isCurrentUser,
}: {
  userVisibilitySetting: UserVisibilitySetting | null | undefined;
  hideSelf?: boolean;
  selfName?: string;
  hideManager?: boolean;
  isCurrentUser?: boolean;
}) => {
  if (!userVisibilitySetting) {
    return "";
  }

  if (userVisibilitySetting["Organization Member"]) {
    return "everyone in my organization";
  }

  const visibilityArr: string[] = [];
  if (userVisibilitySetting.Self && !hideSelf) {
    visibilityArr.push(isCurrentUser ? "you" : selfName);
  }
  const prefix = isCurrentUser ? "your" : `${selfName}'s`;

  if (userVisibilitySetting.Manager && !hideManager) {
    visibilityArr.push(`${prefix} manager`);
  }
  if (userVisibilitySetting["Senior Manager"]) {
    visibilityArr.push(`${prefix} senior manager`);
  }
  if (userVisibilitySetting["Department Leader"]) {
    visibilityArr.push(`${prefix} department leader`);
  }
  if (userVisibilitySetting["Team Leader"]) {
    visibilityArr.push(`${prefix} team leader(s)`);
  }
  if (userVisibilitySetting["Team Member"]) {
    visibilityArr.push(`${prefix} team member(s)`);
  }
  if (userVisibilitySetting["Admin"]) {
    visibilityArr.push(`admin(s)`);
  }

  // on the last item inside of the array pop off, add "and" and push back on
  if (visibilityArr.length > 1) {
    visibilityArr.push(`and ${visibilityArr.pop()}`);
  }

  return visibilityArr.join(visibilityArr.length > 2 ? ", " : " ");
};

export const getYourVisibilityInTeamPersonalityBanner = ({
  userVisibilitySettings,
}: {
  userVisibilitySettings?: UserVisibilitySettings | null;
}) => {
  const userVisibilitySetting =
    userVisibilitySettings?.["Team Personality Continuum"];
  const visibilitySet = getUserVisibilitySetText({
    userVisibilitySetting,
    hideManager: true,
    hideSelf: true,
  });

  return (
    <p>
      Your personality traits are visible to this team's {visibilitySet} as well
      as admins. You can change these visibility settings.
    </p>
  );
};

export const getSuperiorNames = (
  userAccountIds?: number[],
  users?: Record<number, UserInfo>
) => {
  const superiorNames: string[] = [];
  if (!userAccountIds) return "";
  userAccountIds.forEach((id) => {
    const userInfo = users?.[id];
    if (userInfo) {
      const name = `${userInfo.firstName} ${userInfo.lastName}`.trim();
      superiorNames.push(name);
    }
  });

  if (superiorNames.length === 1) {
    return superiorNames[0];
  }
  if (superiorNames.length === 2) {
    return `${superiorNames[0]} and ${superiorNames[1]}`;
  }
  const lastSuperiorName = superiorNames.pop();
  const name = superiorNames.join(", ");

  return `${name}, and ${lastSuperiorName}`;
};

export const updateUserAbsences = (
  userAbsences: UserAbsences,
  updatedAbsence: UserAbsence
): UserAbsences => {
  const { leaveEndDate, leaveStartDate } = updatedAbsence;
  const currentDate = new Date();
  const isEndDateInPast =
    leaveEndDate && new Date(leaveEndDate).getTime() < currentDate.getTime();
  const isStartDateInFuture =
    leaveStartDate &&
    new Date(leaveStartDate).getTime() > currentDate.getTime();
  const isStartDateInPast =
    leaveStartDate &&
    new Date(leaveStartDate).getTime() <= currentDate.getTime();

  // Type of the current absence
  const currentType = userAbsences.activeAbsence ? "active" : "future";
  const isActive = !!userAbsences.activeAbsence;
  const isFuture = currentType === "future";
  const currentPastAbsences = userAbsences.pastAbsences || [];

  // Active + End date in past = Move active to past
  if (isActive && isEndDateInPast) {
    return {
      ...userAbsences,
      activeAbsence: null,
      pastAbsences: [...currentPastAbsences, updatedAbsence],
    };
  }

  // Active + Start date in future = Move active to future
  if (isActive && isStartDateInFuture) {
    return {
      ...userAbsences,
      activeAbsence: null,
      futureAbsence: updatedAbsence,
    };
  }

  // Future + End date in past = Move future to past
  if (isFuture && isEndDateInPast) {
    return {
      ...userAbsences,
      futureAbsence: null,
      pastAbsences: [...currentPastAbsences, updatedAbsence],
    };
  }

  // Future + Start date in past = Move future to active
  if (isFuture && isStartDateInPast) {
    return {
      ...userAbsences,
      futureAbsence: null,
      activeAbsence: updatedAbsence,
    };
  }

  // If active, update active
  if (isActive) {
    return {
      ...userAbsences,
      activeAbsence: updatedAbsence,
    };
  }

  // Else update future
  return {
    ...userAbsences,
    futureAbsence: updatedAbsence,
  };
};

export const getNotificationSettingTitleAndDescription = (
  key: string
): { description: string; title: string } => {
  switch (key) {
    case "teams":
      return {
        title: "Microsoft Teams Reminder",
        description: `Get Microsoft Teams messages for your weekly check-in, reports, and 1:1 meeting prep.`,
      };
    case "calendar":
      return {
        title: "Calendar Sync",
        description: `Enable/disable Microsoft Teams calendar sync.`,
      };
    case "email":
      return {
        title: "1:1 Meeting Prep Email",
        description: `Enable/disable email notifications before your 1:1 meetings.`,
      };
    default:
      return {
        title: key.charAt(0).toUpperCase() + key.slice(1),
        description: `Enable or disable ${key}.`,
      };
  }
};
